/**
 * IndexedDB cache for the messenger.
 *
 * Backs the hybrid pagination strategy: the server holds the canonical
 * history, we keep a per-user IDB mirror so that:
 *
 *   1. Conversation switch paints instantly from cache instead of waiting
 *      on the network round-trip.
 *   2. Scrolling up through older messages stays local while the cache
 *      still has them — only hits the server (`/messages?before=cursor`)
 *      when we run out of local data.
 *   3. Cold start can show the conversation list immediately instead of
 *      blocking on `getChatState`.
 *
 * The cache is best-effort: every operation swallows errors and resolves
 * to a safe default. Private-mode browsers, quota errors, or schema
 * upgrade failures must never block the UI.
 */
import type { Conversation, Message } from "../types";

const DB_NAME_BASE = "slumbr-messenger";
const DB_VERSION = 1;

// The active profile (persona) gates which IndexedDB the cache reads and
// writes. Profile switches reuse the same browser session, so without
// scoping the DB per profile, profile A's chats would render when the user
// switches to profile B. `setActiveProfile` must be called whenever the
// profile changes — the messenger store subscribes to the profile switcher
// and forwards transitions here.
let activeProfileId: string | null = null;

export function setActiveProfile(profileId: string | null): void {
  if (activeProfileId === profileId) return;
  activeProfileId = profileId;
  // Drop any open handle so the next caller opens the right DB.
  if (dbPromise) {
    void dbPromise.then((db) => db?.close());
    dbPromise = null;
  }
}

function getDbName(): string {
  // No profile picked yet — fall back to a "default" bucket. The store
  // also gates hydration on a profile being present, so in practice the
  // default bucket rarely sees writes outside of dev / pre-onboarding.
  return activeProfileId
    ? `${DB_NAME_BASE}:${activeProfileId}`
    : `${DB_NAME_BASE}:default`;
}

const STORE_MESSAGES = "messages";
const STORE_CONVERSATIONS = "conversations";
const STORE_META = "meta";

const IDX_MSG_BY_CONV_TIME = "byConvAndTime";

interface StoredMessage extends Message {
  /** Numeric ms timestamp duplicated for indexing (createdAt itself is
   * string|number; IDB compound indexes need a consistent type). */
  _idxTime: number;
}

interface MetaRecord {
  key: string;
  value: unknown;
}

function toMs(value: number | string | undefined): number {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null);
  if (dbPromise) return dbPromise;

  const dbName = getDbName();
  dbPromise = new Promise((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(dbName, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_MESSAGES)) {
        const store = db.createObjectStore(STORE_MESSAGES, {
          keyPath: ["conversationId", "id"],
        });
        store.createIndex(IDX_MSG_BY_CONV_TIME, ["conversationId", "_idxTime"]);
      }
      if (!db.objectStoreNames.contains(STORE_CONVERSATIONS)) {
        db.createObjectStore(STORE_CONVERSATIONS, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
  });

  return dbPromise;
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => resolve();
    tx.onerror = () => resolve();
  });
}

function reqResult<T>(req: IDBRequest<T>): Promise<T | null> {
  return new Promise((resolve) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
}

function toStored(message: Message): StoredMessage {
  return { ...message, _idxTime: toMs(message.createdAt) };
}

function fromStored(stored: StoredMessage | undefined): Message | undefined {
  if (!stored) return undefined;
  const { _idxTime: _ignored, ...rest } = stored;
  void _ignored;
  return rest;
}

// ─── Conversations ──────────────────────────────────────────────────────────

export async function getCachedConversations(): Promise<Conversation[]> {
  const db = await openDb();
  if (!db) return [];
  return new Promise((resolve) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE_CONVERSATIONS, "readonly");
    } catch {
      resolve([]);
      return;
    }
    const store = tx.objectStore(STORE_CONVERSATIONS);
    const req = store.getAll();
    req.onsuccess = () => resolve((req.result as Conversation[]) ?? []);
    req.onerror = () => resolve([]);
  });
}

export async function setCachedConversations(
  conversations: Conversation[]
): Promise<void> {
  const db = await openDb();
  if (!db) return;
  let tx: IDBTransaction;
  try {
    tx = db.transaction(STORE_CONVERSATIONS, "readwrite");
  } catch {
    return;
  }
  const store = tx.objectStore(STORE_CONVERSATIONS);
  // Replace wholesale: drop everything, then bulk insert. Cheaper than
  // diffing client-side and keeps the cache aligned with the server view.
  store.clear();
  for (const c of conversations) store.put(c);
  await txDone(tx);
}

export async function deleteCachedConversation(
  conversationId: string
): Promise<void> {
  const db = await openDb();
  if (!db) return;
  let tx: IDBTransaction;
  try {
    tx = db.transaction(
      [STORE_CONVERSATIONS, STORE_MESSAGES, STORE_META],
      "readwrite"
    );
  } catch {
    return;
  }
  tx.objectStore(STORE_CONVERSATIONS).delete(conversationId);
  // Cascade-delete messages — IDB has no foreign keys, so do it by index.
  const msgIdx = tx
    .objectStore(STORE_MESSAGES)
    .index(IDX_MSG_BY_CONV_TIME);
  const range = IDBKeyRange.bound(
    [conversationId, -Infinity],
    [conversationId, Infinity]
  );
  const cursorReq = msgIdx.openCursor(range);
  cursorReq.onsuccess = () => {
    const cursor = cursorReq.result;
    if (!cursor) return;
    cursor.delete();
    cursor.continue();
  };
  tx.objectStore(STORE_META).delete(metaKey(conversationId));
  await txDone(tx);
}

// ─── Messages ───────────────────────────────────────────────────────────────

export async function getCachedRecentMessages(
  conversationId: string,
  limit = 50
): Promise<Message[]> {
  const db = await openDb();
  if (!db) return [];
  return new Promise((resolve) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE_MESSAGES, "readonly");
    } catch {
      resolve([]);
      return;
    }
    const idx = tx.objectStore(STORE_MESSAGES).index(IDX_MSG_BY_CONV_TIME);
    const range = IDBKeyRange.bound(
      [conversationId, -Infinity],
      [conversationId, Infinity]
    );
    // Open descending so we naturally grab the newest N first.
    const cursorReq = idx.openCursor(range, "prev");
    const acc: Message[] = [];
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (!cursor || acc.length >= limit) {
        // Return sorted ASC so the UI can append in chronological order.
        resolve(acc.reverse());
        return;
      }
      const msg = fromStored(cursor.value as StoredMessage);
      if (msg) acc.push(msg);
      cursor.continue();
    };
    cursorReq.onerror = () => resolve([]);
  });
}

export async function getCachedMessagesBefore(
  conversationId: string,
  beforeMs: number,
  limit = 50
): Promise<Message[]> {
  const db = await openDb();
  if (!db) return [];
  return new Promise((resolve) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE_MESSAGES, "readonly");
    } catch {
      resolve([]);
      return;
    }
    const idx = tx.objectStore(STORE_MESSAGES).index(IDX_MSG_BY_CONV_TIME);
    // [conversationId, -Infinity] .. [conversationId, beforeMs) — upper
    // exclusive so we don't include the anchor message itself.
    const range = IDBKeyRange.bound(
      [conversationId, -Infinity],
      [conversationId, beforeMs],
      false,
      true
    );
    const cursorReq = idx.openCursor(range, "prev");
    const acc: Message[] = [];
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (!cursor || acc.length >= limit) {
        resolve(acc.reverse());
        return;
      }
      const msg = fromStored(cursor.value as StoredMessage);
      if (msg) acc.push(msg);
      cursor.continue();
    };
    cursorReq.onerror = () => resolve([]);
  });
}

export async function putCachedMessages(
  conversationId: string,
  messages: Message[]
): Promise<void> {
  if (messages.length === 0) return;
  const db = await openDb();
  if (!db) return;
  let tx: IDBTransaction;
  try {
    tx = db.transaction(STORE_MESSAGES, "readwrite");
  } catch {
    return;
  }
  const store = tx.objectStore(STORE_MESSAGES);
  for (const m of messages) {
    if (m.conversationId !== conversationId) continue;
    store.put(toStored(m));
  }
  await txDone(tx);
}

export async function deleteCachedMessage(
  conversationId: string,
  messageId: string
): Promise<void> {
  const db = await openDb();
  if (!db) return;
  let tx: IDBTransaction;
  try {
    tx = db.transaction(STORE_MESSAGES, "readwrite");
  } catch {
    return;
  }
  tx.objectStore(STORE_MESSAGES).delete([conversationId, messageId]);
  await txDone(tx);
}

export async function countCachedMessages(
  conversationId: string
): Promise<number> {
  const db = await openDb();
  if (!db) return 0;
  return new Promise((resolve) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE_MESSAGES, "readonly");
    } catch {
      resolve(0);
      return;
    }
    const idx = tx.objectStore(STORE_MESSAGES).index(IDX_MSG_BY_CONV_TIME);
    const range = IDBKeyRange.bound(
      [conversationId, -Infinity],
      [conversationId, Infinity]
    );
    const req = idx.count(range);
    req.onsuccess = () => resolve(req.result ?? 0);
    req.onerror = () => resolve(0);
  });
}

// ─── Meta (per-conversation pagination state) ───────────────────────────────

export interface ConversationCacheMeta {
  /** Opaque server cursor pointing at the next-older page beyond what we've cached. */
  serverCursor?: string | null;
  /** False once the server has told us there is nothing older. */
  hasMoreOnServer?: boolean;
  /** Wall-clock ms when this conversation was last fully synced from the server. */
  lastSyncedAt?: number;
}

function metaKey(conversationId: string): string {
  return `conv:${conversationId}`;
}

export async function getConversationMeta(
  conversationId: string
): Promise<ConversationCacheMeta> {
  const db = await openDb();
  if (!db) return {};
  return new Promise((resolve) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE_META, "readonly");
    } catch {
      resolve({});
      return;
    }
    const req = tx.objectStore(STORE_META).get(metaKey(conversationId));
    req.onsuccess = () => {
      const rec = req.result as MetaRecord | undefined;
      resolve((rec?.value as ConversationCacheMeta) ?? {});
    };
    req.onerror = () => resolve({});
  });
}

export async function setConversationMeta(
  conversationId: string,
  patch: Partial<ConversationCacheMeta>
): Promise<void> {
  const db = await openDb();
  if (!db) return;
  let tx: IDBTransaction;
  try {
    tx = db.transaction(STORE_META, "readwrite");
  } catch {
    return;
  }
  const store = tx.objectStore(STORE_META);
  const existing = (await reqResult(store.get(metaKey(conversationId)))) as
    | MetaRecord
    | null;
  const merged: ConversationCacheMeta = {
    ...((existing?.value as ConversationCacheMeta) ?? {}),
    ...patch,
  };
  store.put({ key: metaKey(conversationId), value: merged });
  await txDone(tx);
}

// ─── Maintenance ────────────────────────────────────────────────────────────

export async function clearMessengerCache(): Promise<void> {
  const db = await openDb();
  if (!db) return;
  let tx: IDBTransaction;
  try {
    tx = db.transaction(
      [STORE_MESSAGES, STORE_CONVERSATIONS, STORE_META],
      "readwrite"
    );
  } catch {
    return;
  }
  tx.objectStore(STORE_MESSAGES).clear();
  tx.objectStore(STORE_CONVERSATIONS).clear();
  tx.objectStore(STORE_META).clear();
  await txDone(tx);
}
